import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'

export interface ViewportOptions {
  autoRotate?: boolean
  autoRotateSpeed?: number
  backgroundColor?: number
}

export type MapType = 'pbr' | 'default' | 'albedo' | 'normal' | 'roughness' | 'metallic' | 'ao' | 'wireframe'

export class ThreeViewport {
  private container: HTMLElement
  private scene: THREE.Scene
  private camera: THREE.PerspectiveCamera
  private renderer: THREE.WebGLRenderer
  private controls: OrbitControls
  private currentMesh: THREE.Mesh | null = null
  private currentModel: THREE.Group | null = null
  private originalMaterials: Map<THREE.Mesh, THREE.Material | THREE.Material[]> = new Map()
  private wireframeHelper: THREE.LineSegments | null = null
  private animationId: number | null = null
  private isVisible = true
  private initialCameraPosition: THREE.Vector3
  private options: ViewportOptions
  private loader: GLTFLoader
  private viewportId: string
  private userInteracted = false
  private readonly pingPongMin = -45 * (Math.PI / 180)
  private readonly pingPongMax = 45 * (Math.PI / 180)

  constructor(containerSelector: string, options: ViewportOptions = {}) {
    const container = document.querySelector(containerSelector) as HTMLElement
    if (!container) {
      throw new Error(`Container not found: ${containerSelector}`)
    }
    this.container = container
    this.viewportId = containerSelector.replace('#', '').replace('viewport-', '')
    this.options = {
      autoRotate: true,
      autoRotateSpeed: 1.0,
      backgroundColor: 0x1a1a22,
      ...options
    }

    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000)
    this.initialCameraPosition = new THREE.Vector3(0, 0, 4)
    this.camera.position.copy(this.initialCameraPosition)

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    })
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.0

    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.loader = new GLTFLoader()

    const dracoLoader = new DRACOLoader()
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
    this.loader.setDRACOLoader(dracoLoader)

    this.init()
  }

  private init(): void {
    // Setup scene
    this.scene.background = new THREE.Color(this.options.backgroundColor!)

    // Setup renderer
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.updateSize()
    this.container.appendChild(this.renderer.domElement)

    // Hide canvas until models are loaded (managed externally via showContent/showLoading)
    this.renderer.domElement.style.opacity = '0'
    this.renderer.domElement.style.transition = 'opacity 0.4s ease'

    // Setup controls
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.05
    this.controls.enableZoom = true
    this.controls.enablePan = true
    this.controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN
    }
    this.controls.autoRotate = this.options.autoRotate!
    this.controls.autoRotateSpeed = this.options.autoRotateSpeed!
    this.controls.minDistance = 0.5
    this.controls.maxDistance = 15

    // Stop auto-rotate on any user interaction
    this.controls.addEventListener('start', () => {
      this.userInteracted = true
      this.controls.autoRotate = false
    })

    // Setup HDRI environment lighting
    this.setupEnvironment()

    // Create placeholder mesh
    this.createPlaceholderMesh()

    // Handle resize
    this.setupResizeObserver()

    // Re-size after fullscreen changes (ResizeObserver can miss this)
    document.addEventListener('fullscreenchange', () => {
      setTimeout(() => this.updateSize(), 100)
    })

    // Setup visibility observer for performance
    this.setupVisibilityObserver()

    // Start render loop
    this.animate()
  }

  private setupEnvironment(): void {
    const pmremGenerator = new THREE.PMREMGenerator(this.renderer)
    pmremGenerator.compileEquirectangularShader()

    const roomEnv = new RoomEnvironment()
    const envMap = pmremGenerator.fromScene(roomEnv).texture

    this.scene.environment = envMap
    pmremGenerator.dispose()
  }

  private createPlaceholderMesh(): void {
    // Create a stylized geometric shape as placeholder
    const geometry = new THREE.IcosahedronGeometry(1, 1)

    // Create a material with the new purple accent
    const material = new THREE.MeshStandardMaterial({
      color: 0xa855f7,
      metalness: 0.3,
      roughness: 0.7,
      flatShading: true
    })

    this.currentMesh = new THREE.Mesh(geometry, material)
    this.scene.add(this.currentMesh)
  }

  private fitCameraToObject(object: THREE.Object3D): void {
    const box = new THREE.Box3().setFromObject(object)
    const size = box.getSize(new THREE.Vector3())
    const center = box.getCenter(new THREE.Vector3())

    const maxDim = Math.max(size.x, size.y, size.z)
    const fov = this.camera.fov * (Math.PI / 180)
    let cameraDistance = (maxDim / 2) / Math.tan(fov / 2)
    cameraDistance *= 1.4

    // Start 25 degrees to the left of front-on
    const angle = -25 * (Math.PI / 180)
    this.camera.position.set(
      center.x + Math.sin(angle) * cameraDistance,
      center.y,
      center.z + Math.cos(angle) * cameraDistance
    )
    this.camera.lookAt(center)

    this.controls.target.copy(center)
    this.controls.minDistance = cameraDistance * 0.3
    this.controls.maxDistance = cameraDistance * 5
    this.controls.update()

    this.initialCameraPosition.copy(this.camera.position)
  }

  public async loadModel(path: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.loader.load(
        path,
        (gltf) => {
          // Remove current mesh/model
          this.clearCurrentModel()

          this.currentModel = gltf.scene

          // Store original materials and prepare meshes
          this.currentModel.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              // Store original materials
              this.originalMaterials.set(child, child.material)

              // Keep the first mesh as currentMesh for map preview compatibility
              if (!this.currentMesh) {
                this.currentMesh = child
              }
            }
          })

          // Center and scale the model
          const box = new THREE.Box3().setFromObject(this.currentModel)
          const center = box.getCenter(new THREE.Vector3())
          const size = box.getSize(new THREE.Vector3())

          const maxDim = Math.max(size.x, size.y, size.z)
          const scale = 2 / maxDim

          this.currentModel.scale.setScalar(scale)
          this.currentModel.position.sub(center.multiplyScalar(scale))

          this.scene.add(this.currentModel)

          // Fit camera to the scaled model
          this.fitCameraToObject(this.currentModel)

          resolve(true)
        },
        undefined,
        (error) => {
          console.warn(`Failed to load model ${path}:`, error)
          resolve(false)
        }
      )
    })
  }

  private clearCurrentModel(): void {
    if (this.currentModel) {
      this.scene.remove(this.currentModel)
      this.currentModel.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose()
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose())
          } else {
            child.material.dispose()
          }
        }
      })
      this.currentModel = null
    }

    if (this.currentMesh && !this.currentModel) {
      this.scene.remove(this.currentMesh)
      this.currentMesh.geometry.dispose()
      if (Array.isArray(this.currentMesh.material)) {
        this.currentMesh.material.forEach(m => m.dispose())
      } else {
        this.currentMesh.material.dispose()
      }
      this.currentMesh = null
    }

    this.originalMaterials.clear()
    this.removeWireframeHelper()
  }

  private removeWireframeHelper(): void {
    if (this.wireframeHelper) {
      this.scene.remove(this.wireframeHelper)
      // wireframeHelper is a Group containing LineSegments children
      this.wireframeHelper.traverse((child) => {
        if (child instanceof THREE.LineSegments) {
          child.geometry.dispose()
          if (child.material instanceof THREE.Material) {
            child.material.dispose()
          }
        }
      })
      this.wireframeHelper = null
    }
  }

  private addWireframeHelper(): void {
    this.removeWireframeHelper()

    // Get all meshes to create wireframes for
    const meshes: THREE.Mesh[] = []
    if (this.currentModel) {
      this.currentModel.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          meshes.push(child)
        }
      })
    } else if (this.currentMesh) {
      meshes.push(this.currentMesh)
    }

    if (meshes.length === 0) return

    // Create wireframe helper group
    const wireframeGroup = new THREE.Group()
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: 0xa855f7,
      linewidth: 1,
      transparent: true,
      opacity: 0.8
    })

    for (const mesh of meshes) {
      const wireframeGeometry = new THREE.WireframeGeometry(mesh.geometry)
      const helper = new THREE.LineSegments(wireframeGeometry, wireframeMaterial.clone())

      // Copy world transform
      mesh.updateWorldMatrix(true, false)
      helper.applyMatrix4(mesh.matrixWorld)

      wireframeGroup.add(helper)
    }

    this.wireframeHelper = wireframeGroup as unknown as THREE.LineSegments
    this.scene.add(wireframeGroup)
  }

  public async setMeshVariant(variant: 'orc' | 'hardsurface' | 'stylized', modelPath?: string): Promise<void> {
    this.clearCurrentModel()

    // Reset auto-rotate for new model
    this.userInteracted = false
    this.controls.autoRotate = this.options.autoRotate!
    this.controls.autoRotateSpeed = this.options.autoRotateSpeed!

    // Try to load GLB model from provided path or default path
    if (modelPath) {
      const loaded = await this.loadModel(modelPath)
      if (loaded) return
    }

    // Try default path pattern
    const defaultPath = `/assets/CompetitorModels/${this.viewportId}-${variant}.glb`
    const loaded = await this.loadModel(defaultPath)

    if (loaded) {
      return
    }

    // Fallback to placeholder geometry
    let geometry: THREE.BufferGeometry
    let material: THREE.MeshStandardMaterial

    switch (variant) {
      case 'orc':
        // Organic-looking shape
        geometry = new THREE.IcosahedronGeometry(1, 2)
        material = new THREE.MeshStandardMaterial({
          color: 0xa855f7,
          metalness: 0.1,
          roughness: 0.8,
          flatShading: false
        })
        break
      case 'hardsurface':
        // Hard surface prop - box with bevels
        geometry = new THREE.BoxGeometry(1.5, 1.5, 1.5, 2, 2, 2)
        material = new THREE.MeshStandardMaterial({
          color: 0xa855f7,
          metalness: 0.6,
          roughness: 0.3,
          flatShading: true
        })
        break
      case 'stylized':
        // Stylized character - torus knot
        geometry = new THREE.TorusKnotGeometry(0.7, 0.3, 100, 16)
        material = new THREE.MeshStandardMaterial({
          color: 0xa855f7,
          metalness: 0.2,
          roughness: 0.5,
          flatShading: false
        })
        break
      default:
        geometry = new THREE.IcosahedronGeometry(1, 1)
        material = new THREE.MeshStandardMaterial({
          color: 0xa855f7,
          metalness: 0.3,
          roughness: 0.7,
          flatShading: true
        })
    }

    this.currentMesh = new THREE.Mesh(geometry, material)
    this.scene.add(this.currentMesh)
  }

  public setMapPreview(mapType: MapType): void {
    this.removeWireframeHelper()

    // Handle loaded models with multiple meshes
    if (this.currentModel) {
      this.currentModel.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.visible = true
          this.applyMapPreviewToMesh(child, mapType)
        }
      })
    } else if (this.currentMesh) {
      // Handle single placeholder mesh
      this.currentMesh.visible = true
      this.applyMapPreviewToMesh(this.currentMesh, mapType)
    }

    // Add wireframe overlay after all meshes have PBR materials restored
    if (mapType === 'wireframe') {
      this.addWireframeHelper()
    }
  }

  private applyMapPreviewToMesh(mesh: THREE.Mesh, mapType: MapType): void {
    const originalMaterial = this.originalMaterials.get(mesh)

    if ((mapType === 'pbr' || mapType === 'wireframe') && originalMaterial) {
      // Restore original material for PBR view (and wireframe, which overlays on PBR)
      mesh.material = originalMaterial
      return
    }

    if (mapType === 'default') {
      // Neutral grey untextured material — no maps, just plain surface
      const greyMat = new THREE.MeshStandardMaterial({
        color: 0x808080,
        metalness: 0,
        roughness: 0.6
      })
      mesh.material = greyMat
      return
    }

    // For texture channel previews, extract the relevant texture from the original material
    // and display it via emission (unlit)
    const origMat = this.getOriginalStandardMaterial(mesh)

    // Create an unlit material that shows the texture via emissive channel
    const previewMat = new THREE.MeshStandardMaterial({
      color: 0x000000,
      metalness: 0,
      roughness: 1,
      emissive: new THREE.Color(0xffffff),
      emissiveIntensity: 1,
      wireframe: false
    })

    // Disable environment lighting on this material
    previewMat.envMapIntensity = 0

    let texture: THREE.Texture | null = null

    if (origMat) {
      switch (mapType) {
        case 'albedo':
          texture = origMat.map
          break
        case 'normal':
          texture = origMat.normalMap
          break
        case 'roughness':
          texture = origMat.roughnessMap
          break
        case 'metallic':
          // In glTF, metallic is stored in B channel of the same texture as roughness.
          // metalnessMap may be null even when the data exists in roughnessMap.
          texture = origMat.metalnessMap || origMat.roughnessMap
          break
        case 'ao':
          texture = origMat.aoMap
          break
      }
    }

    if (texture) {
      previewMat.emissiveMap = texture

      // In glTF, roughness and metallic are often packed into one texture
      // (G = roughness, B = metallic). If they share the same texture, extract
      // the correct channel. If they're separate textures, show as greyscale.
      if (mapType === 'roughness' || mapType === 'metallic') {
        // Packed if they share the same texture, or if metallic fell back to roughnessMap
        const isPacked = origMat && (origMat.roughnessMap === origMat.metalnessMap || (mapType === 'metallic' && !origMat.metalnessMap && origMat.roughnessMap))
        const channel = mapType === 'roughness' ? 'g' : 'b'

        // Unique cache key so Three.js compiles separate shader programs per channel
        previewMat.customProgramCacheKey = () => `channel-extract-${mapType}-${isPacked ? 'packed' : 'separate'}`

        previewMat.onBeforeCompile = (shader) => {
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <emissivemap_fragment>',
            isPacked
              ? `#ifdef USE_EMISSIVEMAP
                  vec4 emissiveColor = texture2D(emissiveMap, vEmissiveMapUv);
                  float ch = emissiveColor.${channel};
                  totalEmissiveRadiance *= vec3(ch);
                #endif`
              : `#ifdef USE_EMISSIVEMAP
                  vec4 emissiveColor = texture2D(emissiveMap, vEmissiveMapUv);
                  float grey = dot(emissiveColor.rgb, vec3(0.299, 0.587, 0.114));
                  totalEmissiveRadiance *= vec3(grey);
                #endif`
          )
        }
      }
    } else {
      // No texture for this channel — render black
      previewMat.emissive.setHex(0x000000)
    }

    mesh.material = previewMat
  }

  private getOriginalStandardMaterial(mesh: THREE.Mesh): THREE.MeshStandardMaterial | null {
    const original = this.originalMaterials.get(mesh)
    if (!original) return null

    if (original instanceof THREE.MeshStandardMaterial) {
      return original
    }
    if (Array.isArray(original) && original[0] instanceof THREE.MeshStandardMaterial) {
      return original[0]
    }
    return null
  }

  public showLoading(): void {
    // Hide the canvas
    this.renderer.domElement.style.opacity = '0'

    // Show or re-create the loading overlay
    let loading = this.container.querySelector('.viewport-loading') as HTMLElement
    if (!loading) {
      loading = document.createElement('div')
      loading.className = 'viewport-loading'
      this.container.appendChild(loading)
    }
    loading.style.opacity = '1'
  }

  public showContent(): void {
    // Reveal the canvas
    this.renderer.domElement.style.opacity = '1'

    // Fade out and remove loading overlay
    const loading = this.container.querySelector('.viewport-loading') as HTMLElement
    if (loading) {
      loading.style.transition = 'opacity 0.3s ease'
      loading.style.opacity = '0'
      setTimeout(() => loading.remove(), 300)
    }
  }

  public resetView(): void {
    this.camera.position.copy(this.initialCameraPosition)
    this.controls.reset()
    this.userInteracted = false
    this.controls.autoRotate = this.options.autoRotate!
    this.controls.autoRotateSpeed = this.options.autoRotateSpeed!
  }

  public toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      this.container.requestFullscreen().catch(err => {
        console.error('Fullscreen error:', err)
      })
    } else {
      document.exitFullscreen()
    }
  }

  private setupResizeObserver(): void {
    const resizeObserver = new ResizeObserver(() => {
      this.updateSize()
    })
    resizeObserver.observe(this.container)
  }

  private updateSize(): void {
    const width = this.container.clientWidth
    const height = this.container.clientHeight

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    // false = don't set inline styles; CSS width/height: 100% handles visual sizing
    this.renderer.setSize(width, height, false)
  }

  private setupVisibilityObserver(): void {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          this.isVisible = entry.isIntersecting
          if (this.isVisible && !this.userInteracted) {
            this.controls.autoRotate = this.options.autoRotate!
          } else if (!this.isVisible) {
            this.controls.autoRotate = false
          }
        })
      },
      { threshold: 0.1 }
    )
    observer.observe(this.container)
  }

  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate)

    if (this.isVisible) {
      // Ping-pong auto-rotation between ±25 degrees
      if (this.controls.autoRotate) {
        const azimuth = this.controls.getAzimuthalAngle()
        if (azimuth <= this.pingPongMin && this.controls.autoRotateSpeed > 0) {
          this.controls.autoRotateSpeed = -Math.abs(this.controls.autoRotateSpeed)
        } else if (azimuth >= this.pingPongMax && this.controls.autoRotateSpeed < 0) {
          this.controls.autoRotateSpeed = Math.abs(this.controls.autoRotateSpeed)
        }
      }
      this.controls.update()
      this.renderer.render(this.scene, this.camera)
    }
  }

  public dispose(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
    this.controls.dispose()
    this.renderer.dispose()
    this.clearCurrentModel()
  }
}
